import type { MessageDescriptor } from "react-intl";
import { ROLE_LABEL_KEYS, type AssignableRole } from "@/components/match/match-access";
import {
  AccessLevel,
  ListingApplicationAccessLevel,
  type CreateListingAccessDataInput,
  type ListingAccessInput,
  type ListingDetailFragment,
} from "@/graphql/generated/operations";

// Wer an einer Ausschreibung was darf. genesis hält je Person eine Stufe je Bereich (`plan`, `candidates`,
// `applications`) und die Fähigkeit `managesAccess`
// (@external-adr GENESIS-0070-a-listing-is-a-kind-of-project-not-a-shared-item §7). Die Oberfläche zeigt davon
// wie im Match (match-access.ts) drei Rollen und daneben die Bewerbungen als eigene Einstellung
// (Marvin Schmidtke, 2026-10-19):
//
//   Admin          Inhalt und Kandidaten bearbeiten, Mitglieder und Rechte verwalten
//   Bearbeiter:in  Inhalt und Kandidaten bearbeiten
//   Leser:in       Inhalt und Kandidaten ansehen
//
// Bewerbungen bleiben außerhalb der Rolle: sie sind personenbezogen, und wer die Ausschreibung redaktionell
// betreut, soll dadurch keine Bewerbung sehen
// (@external-adr GENESIS-0096-listing-applications-are-read-only-by-their-explicit-readers).
//
// Der Entwurf hält weiter die Stufen, nicht die Rolle. Eine Rolle schreibt erst, wenn jemand sie wählt; eine
// über die API entstandene Mischung (Inhalt schreiben, Kandidaten lesen) bleibt so beim Speichern unangetastet.
//
// Der ERSTELLER steht nicht in der bearbeitbaren Liste. genesis hält seine Zeile mit WRITE auf allen
// Bereichen fest; stünde er hier, ließe er sich scheinbar herabstufen oder entfernen.
//
// Admin ist die Rolle mit `managesAccess`: WRITE auf `plan` verwaltet nichts, und zum Admin ernennt nur, wer
// `canAppointManagers` hat (@external-adr GENESIS-0095-managing-listing-access-is-separate-from-editing-it). Ob ein
// Eintrag änderbar ist, sagt genesis je Eintrag (`canChange`); die Oberfläche leitet das nicht selbst ab.
//
// Bewerbungen kennen als einziger Bereich NONE: Mitgliedschaft liest keine Bewerbung, nur READ oder WRITE.
// Wer neu dazukommt, beginnt dort bei NONE. Wie hoch die aufrufende Person dort vergeben darf, sagt genesis
// (`actions.grantableApplications`); ein höherer Eintrag darf bleiben oder sinken, aber nicht steigen.

export type ListingRole = AssignableRole;

/** Die Rollen in der Reihenfolge, in der sie zur Wahl stehen; Beschriftungen wie im Match. */
export const LISTING_ROLES: readonly ListingRole[] = ["admin", "editor", "reader"];

export const LISTING_ROLE_LABELS: Record<ListingRole, MessageDescriptor> = ROLE_LABEL_KEYS;

export const LISTING_ROLE_LABEL: MessageDescriptor = { id: "share.listings.role", defaultMessage: "Rolle" };

export interface ListingMemberDraft {
  personId: string;
  formattedName: string;
  avatarContent?: string | null;
  avatarMime?: string | null;
  plan: AccessLevel;
  candidates: AccessLevel;
  applications: ListingApplicationAccessLevel;
  /** Die Stufe auf Bewerbungen, wie genesis sie beim Öffnen meldete; null für einen neuen Eintrag. */
  savedApplications: ListingApplicationAccessLevel | null;
  managesAccess: boolean;
  /** Von genesis je Eintrag entschieden; ein im Entwurf neu hinzugefügter Eintrag ist änderbar. */
  canChange: boolean;
}

export const APPLICATIONS_LABEL: MessageDescriptor = {
  id: "share.listings.area.applications",
  defaultMessage: "Bewerbungen",
};

export const APPLICATION_LEVEL_LABELS: Record<ListingApplicationAccessLevel, MessageDescriptor> = {
  [ListingApplicationAccessLevel.None]: { id: "share.listings.level.none", defaultMessage: "Kein Zugriff" },
  [ListingApplicationAccessLevel.Read]: { id: "share.listings.level.read", defaultMessage: "Lesen" },
  [ListingApplicationAccessLevel.Write]: { id: "share.listings.level.write", defaultMessage: "Bearbeiten" },
};

/** Die Stufen auf Bewerbungen in ihrer Reihenfolge; die Position ist der Vergleich der Obergrenze. */
export const APPLICATION_LEVELS: readonly ListingApplicationAccessLevel[] = [
  ListingApplicationAccessLevel.None,
  ListingApplicationAccessLevel.Read,
  ListingApplicationAccessLevel.Write,
];

type RoleAxes = Pick<ListingMemberDraft, "plan" | "candidates" | "managesAccess">;

/**
 * Die Rolle, die ein Eintrag zeigt. Wer irgendwo schreibt, gilt als Bearbeiter:in, wer Zugriffe verwaltet, als
 * Admin: eine Mischung als Leser:in zu zeigen, verschwiege ein Recht, das die Person hat (wie project-access.ts).
 */
export function listingRole(member: RoleAxes): ListingRole {
  if (member.managesAccess) return "admin";
  return member.plan === AccessLevel.Write || member.candidates === AccessLevel.Write ? "editor" : "reader";
}

/** Die Stufen und die Verwaltungsfähigkeit, die eine gewählte Rolle setzt. Bewerbungen bleiben unberührt. */
export function listingRoleAxes(role: ListingRole): RoleAxes {
  const level = role === "reader" ? AccessLevel.Read : AccessLevel.Write;
  return { plan: level, candidates: level, managesAccess: role === "admin" };
}

/**
 * Ob die aufrufende Person diesem Eintrag `level` auf Bewerbungen geben darf: bis zur höheren von zwei Stufen, der
 * Obergrenze `grantable` aus `actions.grantableApplications` und der Stufe, die der Eintrag beim Öffnen hatte (ein
 * neuer Eintrag hatte NONE). Senken bleibt so immer möglich, auch für Verwaltende ohne eigenen Zugriff. Dieselbe
 * Regel wie in genesis (`ListingAccessDelegation`, GENESIS001290): abgelehnt wird nur, was über beiden liegt.
 */
export function applicationLevelAllowed(
  level: ListingApplicationAccessLevel,
  member: Pick<ListingMemberDraft, "savedApplications">,
  grantable: ListingApplicationAccessLevel,
): boolean {
  const rank = (value: ListingApplicationAccessLevel) => APPLICATION_LEVELS.indexOf(value);
  return rank(level) <= Math.max(rank(grantable), rank(member.savedApplications ?? ListingApplicationAccessLevel.None));
}

/**
 * Wer neu dazukommt, ist Leser:in und sieht keine Bewerbung; Bearbeiten und Bewerbungen sind bewusste zweite
 * Entscheidungen.
 */
export function newMember(person: { id: string; formattedName: string }): ListingMemberDraft {
  return {
    personId: person.id,
    formattedName: person.formattedName,
    plan: AccessLevel.Read,
    candidates: AccessLevel.Read,
    applications: ListingApplicationAccessLevel.None,
    savedApplications: null,
    managesAccess: false,
    canChange: true,
  };
}

export function membersFromAccess(
  access: ListingDetailFragment["access"],
  ownerId: string | null | undefined,
): ListingMemberDraft[] {
  return (access ?? [])
    .filter((row) => row.person.id !== ownerId)
    .map((row) => ({
      personId: row.person.id,
      formattedName: row.person.formattedName ?? "",
      plan: row.plan,
      candidates: row.candidates,
      applications: row.applications,
      savedApplications: row.applications,
      managesAccess: row.managesAccess,
      canChange: row.canChange,
    }));
}

/** Die Einträge für `createListing`: dessen Eingabe kennt kein `managesAccess`, ernannt wird danach. */
export function accessInput(members: readonly ListingMemberDraft[]): CreateListingAccessDataInput[] {
  return members.map((member) => ({
    person: { id: member.personId },
    plan: member.plan,
    candidates: member.candidates,
    applications: member.applications,
  }));
}

/** Die Einträge für `setListingAccess`, mit der Verwaltungsfähigkeit. */
export function listingAccessInput(members: readonly ListingMemberDraft[]): ListingAccessInput[] {
  return members.map((member) => ({
    personId: member.personId,
    plan: member.plan,
    candidates: member.candidates,
    applications: member.applications,
    managesAccess: member.managesAccess,
  }));
}

/** Gleicher Sollzustand, unabhängig von der Reihenfolge: nur dann geht etwas hinaus. */
export function sameMembers(a: readonly ListingMemberDraft[], b: readonly ListingMemberDraft[]): boolean {
  const key = (members: readonly ListingMemberDraft[]) =>
    JSON.stringify(
      [...members]
        .sort((x, y) => x.personId.localeCompare(y.personId))
        .map((m) => [m.personId, m.plan, m.candidates, m.applications, m.managesAccess]),
    );
  return key(a) === key(b);
}
